<script setup lang="ts">
import {
  computed,
} from 'vue';

import type { VDisplayCardItem } from '@components/display-card/VDisplayCard/types';
import type {
  DashboardStatsAdminItems,
  DashboardStatsAffiliateItems,
  IDashboardStatsAdminItems,
  IDashboardStatsItemAffiliate,
} from '@modules/dashboard/types';
import VDisplayCard from '@components/display-card/VDisplayCard/VDisplayCard.vue';
import { useConfig } from '@modules/config/composables';
import {
  useDashboardFilters,
  useDashboardTranslationGroup,
} from '@modules/dashboard/composables';
import { useDashboardStatsQuery } from '@modules/dashboard/queries';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';

const { config } = useConfig();
const { merchantId, period } = useDashboardFilters();
const { data: stats, isLoading } = useDashboardStatsQuery(merchantId);
const dashboardStatsBySelectedPeriod = computed(() => stats.value?.[period.value]);
const { translationGroup } = useDashboardTranslationGroup();
const { getTranslation } = useI18n();
const { isAdminNamespace } = useCurrentNamespace();

const displayItemsAdmin: DashboardStatsAdminItems[] = [
  'registration_count',
  'first_deposit_count',
  'ngr',
  'profit',
  'deposits',
  'withdrawals',
];
const displayItemsAffiliate: DashboardStatsAffiliateItems[] = [
  'views_count',
  'clicks_count',
  'registration_count',
  'client_count',
  'profit',
  'fixed_payment',
];

const displayItems = computed<VDisplayCardItem[]>(() => {
  if (isAdminNamespace.value) {
    const values = dashboardStatsBySelectedPeriod.value as IDashboardStatsAdminItems;
    const data = displayItemsAdmin.map((v) => ({
      label: getTranslation(translationGroup.value, `STATS_${v.toUpperCase()}`, { replace: { currency: config.value?.currency } }).value,
      value: values ? values?.[v] ?? '-' : '',
    }));
    return data || Array.from(Array.from({ length: displayItemsAdmin.length }));
  }

  const values = dashboardStatsBySelectedPeriod.value as IDashboardStatsItemAffiliate;
  const data = displayItemsAffiliate.map((v) => ({
    label: getTranslation(translationGroup.value, `STATS_${v.toUpperCase()}`, { replace: { currency: config.value?.currency } }).value,
    value: values ? values?.[v] ?? '-' : '',
  }));
  return data || Array.from(Array.from({ length: displayItemsAffiliate.length }));
});
</script>

<template>
  <div v-data-qa-auto-id="'TheDashboardStatsDisplay'" class="flex flex-wrap gap-4">
    <VDisplayCard
      v-for="item in displayItems"
      :key="item.label"
      :label="item.label"
      :value="item.value"
      :loading="isLoading"
      class="grow basis-full md:basis-1/4 lg:basis-1/12"
    />
  </div>
</template>
